/**
 * Floating Action Button (FAB)
 * A small button fixed to the bottom-right corner that toggles the chat window.
 * Shows bot.png image (light/dark themed) and includes an unread message indicator badge.
 * When the chat is open and has been dragged from default position, shows a restore button.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAIContext } from './AIContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import botIcon from '../../assets/images-night/bot.png';
import darkBotIcon from '../../assets/images/bot.png';

// Storage key to match AIChatBot
const POSITION_STORAGE_KEY = 'balancio_ai_position';

interface Position {
  x: number;
  y: number;
}

const FloatingButton: React.FC = () => {
  const { toggleOpen, isOpen, messages } = useAIContext();
  const { isDark } = useThemeContext();

  const hasNewMessages = messages.length > 0 && !isOpen;

  // Check if mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track if chat is not at default position
  const [isNotDefaultPosition, setIsNotDefaultPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(POSITION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.x !== 0 || parsed.y !== 0;
      }
    } catch {}
    return false;
  });

  // Listen for custom position-changed events from AIChatBot
  useEffect(() => {
    const handlePositionChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        setIsNotDefaultPosition(detail.x !== 0 || detail.y !== 0);
      }
    };
    window.addEventListener('balancio-position-changed', handlePositionChanged);
    return () => window.removeEventListener('balancio-position-changed', handlePositionChanged);
  }, []);

  const handleRestorePosition = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x: 0, y: 0 }));
    setIsNotDefaultPosition(false);
    // Dispatch custom event so AIChatBot can pick it up without page reload
    window.dispatchEvent(new CustomEvent('balancio-reset-position'));
  }, []);

  const buttonSize = isMobile ? 'w-12 h-12' : 'w-14 h-14';
  const bottomOffset = isMobile ? 'bottom-20' : 'bottom-6';
  const rightOffset = isMobile ? 'right-4' : 'right-6';

  return (
    <>
      {/* Main toggle button */}
      <button
        onClick={toggleOpen}
        title={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
        className={`fixed ${bottomOffset} ${rightOffset} z-40 flex items-center justify-center ${buttonSize} rounded-full shadow-lg transition-all duration-300 hover:scale-110 active:scale-95`}
        style={{
          backgroundColor: isOpen ? 'var(--danger)' : 'var(--primary-variant)',
          color: '#FFFFFF',
        }}
        aria-label={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
      >
        {isOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-7 sm:w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <img
            src={isDark ? darkBotIcon : botIcon}
            alt="AI Assistant"
            className="rounded-full object-cover"
            style={{
              width: isMobile ? '28px' : '40px',
              height: isMobile ? '28px' : '40px',
            }}
          />
        )}

        {/* Badge indicator for new messages */}
        {hasNewMessages && (
          <span
            className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
            style={{
              backgroundColor: 'var(--danger)',
              color: '#FFFFFF',
            }}
          >
            {messages.length}
          </span>
        )}
      </button>

      {/* Restore position button - only visible when chat is open and has been moved */}
      {isOpen && isNotDefaultPosition && !isMobile && (
        <button
          onClick={handleRestorePosition}
          title="Reset chat to default position"
          className="fixed bottom-24 right-6 z-40 flex items-center justify-center w-10 h-10 rounded-full shadow-lg transition-all duration-300 hover:scale-110 active:scale-95"
          style={{
            backgroundColor: 'var(--bg-grey-light)',
            color: 'var(--dark)',
            border: '1px solid var(--grey-light)',
          }}
          aria-label="Reset chat position"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      )}
    </>
  );
};

export default FloatingButton;
